import { Cog, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { ServiceUser } from "@/api/types";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDestructiveDialog } from "@/components/ui/confirm-destructive-dialog";
import { Input } from "@/components/ui/input";
import { ServiceUserFormDialog } from "@/features/service-users/components/ServiceUserFormDialog";
import {
  useServiceUsers,
  useUpdateServiceUser,
} from "@/features/service-users/hooks/useServiceUsers";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

export function ServiceUsersListPage() {
  const { t } = useTranslation("serviceUsers");
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const canCreate = useCan(Action.SERVICE_USER_CREATED);

  const filters = useMemo(
    () => ({
      q: q.trim() || undefined,
      is_active: showInactive ? undefined : true,
    }),
    [q, showInactive],
  );

  const { data, isLoading, isError, error, refetch } =
    useServiceUsers(filters);

  if (isLoading) return <LoadingState />;
  if (isError)
    return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("title")}
        description={t("description")}
        actions={
          <Button onClick={() => setCreateOpen(true)} disabled={!canCreate}>
            <Plus className="h-4 w-4" />
            {t("new")}
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder={t("filters.search")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
        />
        <Button
          variant={showInactive ? "secondary" : "outline"}
          onClick={() => setShowInactive((v) => !v)}
        >
          {showInactive ? t("filters.hideInactive") : t("filters.showInactive")}
        </Button>
      </div>

      {data && data.length === 0 ? (
        <EmptyState
          title={t("empty.title")}
          description={t("empty.description")}
        />
      ) : (
        <ul className="shadow-card divide-y divide-line rounded-2xl border border-line bg-surface">
          {data?.map((su) => (
            <ServiceUserRow key={su.id} serviceUser={su} />
          ))}
        </ul>
      )}

      <ServiceUserFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function ServiceUserRow({ serviceUser }: { serviceUser: ServiceUser }) {
  const { t } = useTranslation("serviceUsers");
  const update = useUpdateServiceUser(serviceUser.id);
  const canManage = useCan(Action.SERVICE_USER_UPDATED);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function toggleActive() {
    try {
      await update.mutateAsync({ is_active: !serviceUser.is_active });
      toast.success(
        serviceUser.is_active
          ? t("toasts.deactivated")
          : t("toasts.reactivated"),
      );
      setConfirmOpen(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-canvas/60">
      <div className="flex flex-col">
        <span className="font-medium text-ink-950">{serviceUser.name}</span>
        {!serviceUser.is_active ? (
          <Badge variant="destructive" className="mt-1 w-fit">
            {t("badges.inactive")}
          </Badge>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild size="sm" variant="outline">
          <Link to={`/admin/service-users/${serviceUser.id}`}>
            <Cog className="h-4 w-4" />
            {t("actions.manage")}
          </Link>
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!canManage || update.isPending}
          onClick={() => {
            if (serviceUser.is_active) setConfirmOpen(true);
            else void toggleActive();
          }}
        >
          {serviceUser.is_active
            ? t("actions.deactivate")
            : t("actions.activate")}
        </Button>
      </div>

      <ConfirmDestructiveDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("deactivate.title")}
        description={t("deactivate.description")}
        resourceName={serviceUser.name}
        resourceKind={t("deactivate.resourceKind")}
        confirmLabel={t("deactivate.confirm")}
        onConfirm={toggleActive}
        isPending={update.isPending}
      />
    </li>
  );
}
